import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import Grid from "@mui/material/GridLegacy";
import Box from "@mui/material/Box";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { count, isDefined } from '@components/config/helpers';
import { SEARCH_SECTION_ID } from '@components/config/constants';
import CategoryProductExtras from './Extras/CategoryProductExtras';

const CategoryProductPriceLabels = dynamic(() => import('./PriceSection/CategoryProductPriceLabels'));
const CategoryProductImage = dynamic(() => import('./CategoryProductImage'));
const CategoryProductVariants = dynamic(() => import('./Variants/CategoryProductVariants'), { ssr: false });
const CategoryProductVariantImageContainer = dynamic(() => import('./Variants/CategoryProductVariantImageContainer'), { ssr: false });
const CategoryProductPriceSection = dynamic(() => import('./PriceSection/CategoryProductPriceSection'), { ssr: true });

const CardProps = {
    raised: false,
    elevation: 2,
    sx: {
        marginBottom: {
            xs: '0.5rem',
            md: 0
        },
        borderLeft: '1px solid lightgrey',
        borderRight: '1px solid lightgrey',
        minWidth: '100%',
        maxHeight: {
            lg: 'fit-content',
            md: 'fit-content',
            sm: '90%'
        },
        minHeight: {
            lg: 'fit-content',
            md: 'fit-content',
            sm: '100%'
        },
    }
};

const CategoryProductTile = ({
    props,
    long_term,
    product,
    product_idx,
    source,
    show_images = true,
    gridProps = {},
    elevation,
    addToQuoteComponent = false,
    package_core,
    extrasHook
}) => {
    const show_product_variants = useSelector((state) => state.globalReducer.template_settings.show_product_variants);
    const cat_slug = useSelector((state) => state.categoryReducer?.categoryState?.category?.slug);

    const [isVisible, setIsVisible] = useState(!show_images ? product_idx < 8 : true);
    const productRef = useRef(null);
    useEffect(() => {
        setTimeout(() => {
            const observerCallback = (entries, observer) => {
                entries.forEach(entry => {
                    if (!isVisible && entry.isIntersecting) {
                        setIsVisible(entry.isIntersecting);
                    }
                });
            };

            const observer = new IntersectionObserver(observerCallback, {
                rootMargin: '50px 0px',
                threshold: [0, 0.1, 1.0]
            });

            if (productRef.current) {
                observer.observe(productRef.current);
            }

            return () => {
                observer.disconnect();
            };
        }, 0);
    }, [productRef.current]);

    const productData = useMemo(() => ({
        image: product.image,
        product_name: product.product_name,
        product_code: product.product_code,
        on_offer: product.on_offer,
        canonical_category_slug: cat_slug ? cat_slug : product.canonical_category_slug,
        section_slug: product.section_slug,
        product_slug: product.product_slug,
        is_designer_original: product.is_designer_original,
        is_eco_friendly: product.carbon_footprint,
        product_idx,
        long_term,
    }), [product]);

    const productProps = useMemo(() => ({
        product_id: product.product_id,
        product_name: product.product_name,
        product_code: product.product_code,
        product_slug: product.product_slug,
        package_core_group_id: product?.package_core_group_id,
        bow_color_option: product.bow_color_option,
        pad_color_option: product.pad_color_option,
        image: product.image,
        color: product.color,
        canonical_category_slug: cat_slug ? cat_slug : product.canonical_category_slug,
        section_slug: product.section_slug,
        on_offer: product.on_offer,
        poa_price: product.poa_price,
        orderable: product?.orderable,
        price_breaks: product.price_breaks,
        product_price: product.product_price,
        new_price: product.new_price,
        addons: product.addons,
        extras: product.extras,
        extra_groups: product.extra_groups,
        has_extras: product.has_extras,
        extras_count: product.extras_count,
        pad_options: product.pad_options,
        all_in_price: product.all_in_price,
        discounted_price: product.discounted_price,
        product_extra_id: product.product_extra_id,
        buy_price: product.buy_price
    }), [product]);

    const variantData = useMemo(() => ({
        long_term,
        source,
    }), [long_term, source]);

    const priceLabels = useMemo(() => ({
        product_name: product.product_name,
        product_code: product.product_code,
        section_slug: product.section_slug,
        canonical_category_slug: cat_slug ? cat_slug : product.canonical_category_slug,
        product_slug: product.product_slug,
    }), [product]);

    const priceSection = useMemo(() => ({
        product: productProps,
        page: 'category',
        long_term,
        sectionID: source === 'search' ? SEARCH_SECTION_ID : 2,
        source,
        extrasHook,
        addToQuoteComponent: addToQuoteComponent
    }), [productProps, long_term, source]);

    const productExtraData = useMemo(() => [...product.product_extra_list || []]);

    return (
        <Grid
            item
            {...gridProps}
            ref={productRef}
            className={`product ${isVisible ? 'visible' : 'invisible'}`}
            sx={{
                marginTop: 0,
            }}
        >
            <Card
                sx={{
                    marginBottom: '0.5rem',
                    borderLeft: '1px solid lightgrey',
                    borderRight: '1px solid lightgrey',
                    minWidth: '100%',
                    maxHeight: 'fit-content',
                    minHeight: 'fit-content',
                }}
                raised={false}
                elevation={elevation}
            >
                <CategoryProductImage {...productData} package_core={package_core} />

                <CardContent className={`mt-0 mb-0 pb-0 pr-2 pl-2 p-0`}>
                    {(source !== 'search' && show_product_variants === 'yes') && (
                        <Box
                            sx={{
                                height: 'fit-content',
                                minHeight: '58px'
                            }}
                            className='d-flex w-100 mt-0 mb-0'
                        >
                            {count(productExtraData) > 0 ? (
                                <>
                                    <CategoryProductExtras
                                        source={source}
                                        product={{
                                            canonical_category_slug: product.canonical_category_slug,
                                            product_id: product.product_id,
                                            product_slug: product.product_slug,
                                            long_term: long_term,
                                            product_extras: [...productExtraData]
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    {isDefined(product.product_variants) && count(product.product_variants) > 0 ? (
                                        <CategoryProductVariants
                                            {...variantData}
                                            product={{
                                                section_slug: product.section_slug,
                                                canonical_category_slug: product.canonical_category_slug,
                                                product_id: product.product_id,
                                                product_slug: product.product_slug,
                                                product_variants: product.product_variants,
                                            }}
                                        />
                                    ) : (
                                        <CategoryProductVariantImageContainer
                                            {...variantData}
                                            variant={{
                                                product_variant_product_id: product.product_variant_product_id,
                                                product_slug: product.product_slug,
                                                section_slug: product.section_slug,
                                                canonical_category_slug: product.canonical_category_slug,
                                                product_variant_label: product.product_variant_label,
                                                product_name: product.product_name,
                                                image: product.image,
                                            }}
                                            package_core={package_core}
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    <CategoryProductPriceLabels {...priceLabels} package_core={package_core} />
                    <CategoryProductPriceSection {...priceSection} />

                </CardContent>
            </Card>
        </Grid>

    );
};

export default CategoryProductTile;
